'use no memo';

import type { Table as TableInstance } from '@tanstack/react-table';
import type { ComponentType, MouseEvent, ReactElement } from 'react';

import { cn } from '@repo/lib/cn';
import { flexRender } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon } from 'lucide-react';
import { startTransition, useCallback, useMemo, useState } from 'react';

import TanstackTableActions from '@/components/features/manage/tanstack-table/TanstackTableActions';
import TanstackTableColumnsDropdown from '@/components/features/manage/tanstack-table/TanstackTableColumnsDropdown';
import TanstackTableDateRangeFilter from '@/components/features/manage/tanstack-table/TanstackTableDateRangeFilter';
import TanstackTableFacetedFilters from '@/components/features/manage/tanstack-table/TanstackTableFacetedFilters';
import TanstackTableLoading from '@/components/features/manage/tanstack-table/TanstackTableLoading';
import TanstackTableRows from '@/components/features/manage/tanstack-table/TanstackTableRows';
import TanstackTableSearch from '@/components/features/manage/tanstack-table/TanstackTableSearch';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useMediaQuery } from '@/hooks/media-query';

export interface TableAddGroupItem {
  id: number;
  label: string;
  icon?: ReactElement;
  func?: () => void;
}

export type FilterType = 'faceted' | 'dateRange';

export interface FilterByOption {
  id: string;
  title: string;
  type?: FilterType;
  options?: {
    label: string;
    value: string | number | boolean;
    icon?: ComponentType<{ className?: string }>;
  }[];
}

export interface TanstackTableProps<TData> {
  table: TableInstance<TData>;
  label?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  addGroup?: TableAddGroupItem[];
  filterBy?: FilterByOption[];
  className?: string;
  actionsComponent?: ReactElement;
  onRemoveSelectedRows?: () => void;
  onGlobalFilterChange?: (value: string) => void;
  onRowClick?: (event: MouseEvent, row: ReturnType<TableInstance<TData>['getRowModel']>['rows'][number], rowIndex: number) => void;
}

interface TanstackTableHeaderContentProps<TData> {
  table: TableInstance<TData>;
  rowClassName?: string;
  getHeadClassName?: (headerId: string) => string | undefined;
}

/** Check if a filter value is considered "active" (has meaningful data) */
const isFilterValueActive = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.values(value).some((v) => v !== undefined && v !== null && v !== '');
  }
  return true;
};

export function TanstackTableHeaderContent<TData>({ table, rowClassName, getHeadClassName }: TanstackTableHeaderContentProps<TData>) {
  return (
    <>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className={cn('hover:bg-surface-primary-hover', rowClassName)}>
          {headerGroup.headers.map((header) => {
            const sortState = header.column.getIsSorted();
            const canSort = header.column.getCanSort();
            return (
              <TableHead
                key={header.id}
                className={cn('px-3 py-1 align-middle whitespace-nowrap', getHeadClassName?.(header.id))}
                aria-sort={sortState === 'asc' ? 'ascending' : sortState === 'desc' ? 'descending' : 'none'}
              >
                {header.isPlaceholder ? null : canSort ? (
                  <div
                    className={cn('flex h-full cursor-pointer items-center justify-between gap-2 select-none')}
                    onClick={header.column.getToggleSortingHandler()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        header.column.getToggleSortingHandler()?.(e);
                      }
                    }}
                    tabIndex={0}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {sortState === 'asc' && <ChevronUpIcon className="size-4 shrink-0 opacity-60" aria-hidden="true" />}
                    {sortState === 'desc' && <ChevronDownIcon className="size-4 shrink-0 opacity-60" aria-hidden="true" />}
                    {!sortState && <span className="size-4" aria-hidden="true" />}
                  </div>
                ) : (
                  flexRender(header.column.columnDef.header, header.getContext())
                )}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </>
  );
}

export default function TanstackTable<TData>({ table, label, isLoading, addGroup, actionsComponent, filterBy, onRemoveSelectedRows, onGlobalFilterChange, onRowClick }: TanstackTableProps<TData>) {
  const breakpoint = useMediaQuery('md');

  // Use TanStack Table's reactive state
  const hasSelectedRows = table.getSelectedRowModel().rows.length > 0;
  const columnFilters = table.getState().columnFilters;

  // Track user-added filter IDs (filters explicitly opened by user)
  const [userAddedFilterIds, setUserAddedFilterIds] = useState<string[]>([]);

  // Compute active filter IDs from column filter state
  const activeFilterIds = useMemo(() => {
    if (!filterBy) return new Set<string>();
    const active = new Set<string>();
    for (const { id } of filterBy) {
      const filter = columnFilters.find((f) => f.id === id);
      if (filter && isFilterValueActive(filter.value)) {
        active.add(id);
      }
    }
    return active;
  }, [filterBy, columnFilters]);

  // Visible filters: union of user-added and active filters, preserving order
  const visibleFilters = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];

    // First add user-added filters in order
    for (const id of userAddedFilterIds) {
      if (!seen.has(id)) {
        seen.add(id);
        result.push(id);
      }
    }

    // Then add active filters that weren't user-added
    for (const id of activeFilterIds) {
      if (!seen.has(id)) {
        seen.add(id);
        result.push(id);
      }
    }

    return result;
  }, [userAddedFilterIds, activeFilterIds]);

  // Available filters (not currently visible)
  const availableFilters = useMemo(() => {
    if (!filterBy) return [];
    const visibleSet = new Set(visibleFilters);
    return filterBy.filter(({ id }) => !visibleSet.has(id));
  }, [filterBy, visibleFilters]);

  const handleAddFilter = useCallback((filterId: string) => {
    setUserAddedFilterIds((prev) => (prev.includes(filterId) ? prev : [...prev, filterId]));
  }, []);

  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      // Clear the filter value (non-urgent - filtering is expensive)
      startTransition(() => {
        table.getColumn(filterId)?.setFilterValue(undefined);
      });
      // Remove from user-added list
      setUserAddedFilterIds((prev) => prev.filter((id) => id !== filterId));
    },
    [table]
  );

  return (
    <>
      <header className={'bg-background border-border-primary w-full rounded-2xl border'}>
        {/* Header row: h2, search/columns (md+), and actions */}
        <div className={'flex w-full items-center justify-between gap-3 p-4'}>
          <h2 className={'text-xl font-semibold'}>{label}</h2>
          <div className={'flex items-center gap-2'}>
            {/* Search and Columns - visible on md+ screens */}
            <div className="hidden items-center gap-2 md:flex">
              {onGlobalFilterChange && <TanstackTableSearch setGlobalFilter={onGlobalFilterChange} />}
              <TanstackTableColumnsDropdown table={table} />
            </div>
            {/* Actions - visible on mobile, next to h2 */}
            <div className="md:hidden">
              <TanstackTableActions addGroup={addGroup} actionsComponent={actionsComponent} removeRows={onRemoveSelectedRows ?? (() => {})} isSelection={hasSelectedRows} />
            </div>
          </div>
        </div>
        <Separator />
        {/* Search and Columns row - mobile only */}
        <div className={'flex items-center gap-2 p-3 pb-0 md:hidden'}>
          {onGlobalFilterChange && <TanstackTableSearch setGlobalFilter={onGlobalFilterChange} className="min-w-0 flex-1" />}
          <TanstackTableColumnsDropdown table={table} />
        </div>
        {/* Filters row */}
        <div className={'flex flex-col-reverse justify-between gap-2 p-4 sm:flex-row'}>
          {/* Filters container - horizontal scroll on mobile, wrap on desktop */}
          <div className="flex items-center gap-1.5 overflow-x-auto md:flex-wrap md:overflow-x-visible">
            {filterBy &&
              visibleFilters.map((filterId) => {
                const filterConfig = filterBy.find(({ id }) => id === filterId);
                if (!filterConfig) return null;
                const { id, title, type = 'faceted', options } = filterConfig;
                const column = table.getColumn(id);
                if (type === 'dateRange') {
                  return (
                    <div key={id} className="shrink-0">
                      <TanstackTableDateRangeFilter column={column} title={title} onRemove={() => handleRemoveFilter(id)} />
                    </div>
                  );
                }

                if (type === 'faceted') {
                  return (
                    <div key={id} className="shrink-0">
                      <TanstackTableFacetedFilters column={column} title={title} options={options || []} onRemove={() => handleRemoveFilter(id)} />
                    </div>
                  );
                }
                return null;
              })}
            {availableFilters.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="shrink-0">
                    <PlusIcon />
                    Dodaj filtr
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuLabel>Dodaj filtr</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    {availableFilters.map(({ id, title }) => (
                      <DropdownMenuItem key={id} onSelect={() => handleAddFilter(id)}>
                        {title}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {/* Actions - visible on md+ screens, next to filters */}
          <div className="hidden md:block">
            <TanstackTableActions addGroup={addGroup} actionsComponent={actionsComponent} removeRows={onRemoveSelectedRows ?? (() => {})} isSelection={hasSelectedRows} />
          </div>
        </div>
      </header>
      {breakpoint ? (
        <div className="border-border-primary bg-background relative h-full w-full overflow-hidden rounded-2xl border">
          <div className="h-full w-full overflow-x-auto">
            <Table>
              <TableHeader className={'bg-background/90 sticky top-0 z-10 backdrop-blur-xs'}>
                <TanstackTableHeaderContent table={table} />
              </TableHeader>
              <TableBody>{isLoading ? <TanstackTableLoading table={table} /> : <TanstackTableRows table={table} colSpan={table.getAllColumns().length} onRowClick={onRowClick} />}</TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <div className={'flex h-full w-full flex-col gap-2'}>
          {isLoading ? <TanstackTableLoading table={table} /> : <TanstackTableRows table={table} colSpan={table.getAllColumns().length} onRowClick={onRowClick} />}
        </div>
      )}
    </>
  );
}
